import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Input, Select, Space, Table, Tag, Typography, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import LoadingCard from "components/LoadingCard/LoadingCard";
import { CONTROL } from "pages/MarketingPages/FieldInput";
import { downloadSubscribersCsv, errorMessage, fetchSubscribers } from "./subscribersApi";

const { Title, Text } = Typography;
const { Search } = Input;

const LANG_OPTIONS = [
  { value: "bg", label: "Български" },
  { value: "en", label: "Английски" },
];

const STATUS_LABELS = {
  subscribed: "Абониран",
  unsubscribed: "Отписан",
  all: "Всички",
};

const STATUS_OPTIONS = [
  { value: "subscribed", label: STATUS_LABELS.subscribed },
  { value: "unsubscribed", label: STATUS_LABELS.unsubscribed },
  { value: "all", label: STATUS_LABELS.all },
];

const PAGE_SIZE = 50;

const COLUMNS = [
  { title: "Имейл", dataIndex: "email", key: "email" },
  {
    title: "Език",
    dataIndex: "lang",
    key: "lang",
    render: (lang) => <Tag>{lang}</Tag>,
  },
  { title: "Източник", dataIndex: "source", key: "source" },
  {
    title: "Статус",
    dataIndex: "status",
    key: "status",
    render: (status) => (
      <Tag color={status === "unsubscribed" ? "default" : "green"}>
        {STATUS_LABELS[status] ?? status}
      </Tag>
    ),
  },
  {
    title: "Съгласие на",
    dataIndex: "consentedAt",
    key: "consentedAt",
    render: (value) => (value ? dayjs(value).format("D MMM YYYY HH:mm") : "—"),
  },
  {
    title: "Дата на създаване",
    dataIndex: "createdAt",
    key: "createdAt",
    render: (value) => (value ? dayjs(value).format("D MMM YYYY HH:mm") : "—"),
  },
];

/**
 * Who asked for the newsletter, from either property's footer form (DAB-846).
 *
 * Read-only: there is nothing to edit here, only to find and export — support
 * looking someone up by email, and a CSV for whoever actually sends the
 * campaigns, which isn't this admin.
 */
export default function Subscribers() {
  const [subscribers, setSubscribers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lang, setLang] = useState(undefined);
  const [status, setStatus] = useState("subscribed");
  const [q, setQ] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const filters = { page, limit: PAGE_SIZE, lang, status, q: q || undefined };

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const result = await fetchSubscribers(filters);
      setSubscribers(result.subscribers);
      setTotal(result.total);
    } catch (error) {
      setLoadError(errorMessage(error));
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, lang, status, q]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = async () => {
    setIsExporting(true);
    try {
      await downloadSubscribersCsv({ lang, status, q: q || undefined });
    } catch (error) {
      message.error(errorMessage(error));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div style={{ padding: 16, maxWidth: 960, margin: "0 auto" }}>
      <Space direction="vertical" size={4} style={{ width: "100%", marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Абонати
        </Title>
        <Text type="secondary">
          Всички, които са се абонирали за бюлетина от приложението или сайта.
        </Text>
      </Space>

      <Space wrap size={8} style={{ marginBottom: 16, width: "100%" }}>
        <Select
          style={{ ...CONTROL, width: 180 }}
          allowClear
          placeholder="Всички езици"
          options={LANG_OPTIONS}
          value={lang}
          onChange={(value) => {
            setLang(value);
            setPage(1);
          }}
        />
        <Select
          style={{ ...CONTROL, width: 160 }}
          options={STATUS_OPTIONS}
          value={status}
          onChange={(value) => {
            setStatus(value);
            setPage(1);
          }}
        />
        <Search
          style={{ ...CONTROL, width: 260 }}
          allowClear
          placeholder="Търсене по имейл"
          onSearch={(value) => {
            setQ(value.trim());
            setPage(1);
          }}
        />
        <Button
          style={CONTROL}
          icon={<DownloadOutlined />}
          loading={isExporting}
          onClick={exportCsv}
        >
          Изтегли CSV
        </Button>
      </Space>

      {isLoading && <LoadingCard />}

      {!isLoading && loadError && (
        <Alert
          type="error"
          showIcon
          message="Неуспешно зареждане на абонатите"
          description={loadError}
          action={<Button onClick={load}>Опитай отново</Button>}
        />
      )}

      {!isLoading && !loadError && (
        <Table
          rowKey="_id"
          columns={COLUMNS}
          dataSource={subscribers}
          scroll={{ x: "max-content" }}
          locale={{ emptyText: "Все още няма абонати." }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            onChange: setPage,
            showSizeChanger: false,
          }}
        />
      )}
    </div>
  );
}
